import { PHASE_PRODUCTION_BUILD } from 'next/constants'

import { Post } from 'src/components/Post/Post'
import { config } from 'src/config'
import { POST_TYPE_BLOG_POST } from 'src/constants'
import { fetchPost } from 'src/data/providers/fetchPost'
import { fetchPosts } from 'src/data/providers/fetchPosts'
import { getLanguageFromLocale, getLocaleFromLanguage } from 'src/functions/i18n/localeFns'
import { getCache } from 'src/lib/keyv'

import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next'
import type { Language } from 'src/enum/Language'
import type { Locale } from 'src/enum/Locale'
import type { Post as IPost } from 'src/types/Post'

const getStaticProps: GetStaticProps = async ({ params }) => {
  const { lang, slug } = params as { lang: Language; slug: string }
  const locale = getLocaleFromLanguage(lang)
  const cache = getCache('posts')

  const cachedPost = await cache.get(`${slug}.${locale}`)
  const post: IPost | null = cachedPost || (await fetchPost(POST_TYPE_BLOG_POST, slug, locale))

  if (!post) {
    return { notFound: true }
  }

  return { props: { post, type: 'blog' } }
}

const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
    const paths: GetStaticPathsResult['paths'] = []
    const cache = getCache('posts')

    await Promise.all(
      config.locales.map(async (locale) => {
        const posts = await fetchPosts(POST_TYPE_BLOG_POST, locale as Locale)
        const lang = getLanguageFromLocale(locale as Locale)

        posts.forEach((post) => {
          cache.set(`${post.slug}.${locale}`, post)
          paths.push({
            params: {
              slug: post.slug,
              lang,
            },
          })
        })
      }),
    )

    return {
      paths,
      fallback: false,
    }
  }

  return {
    paths: [],
    fallback: 'blocking',
  }
}

export { getStaticProps, getStaticPaths }

export default Post
